
import { Mixins, Component, Prop, Ref, Watch } from "vue-property-decorator";
import { onResize } from "_mixins";
import { SmartImage, SmartImageProps, SmartText, WYSIWYG } from "_components";
import { ThemeLink } from "_types";
import { GSAP } from "_core";

import MainButton from "./HeroButton.vue";
import ArrowButton from "./ArrowButton.vue";
import { HomePageHeroAlternativeBlockType } from "../../types";

@Component({
  name: "HeroAlternativeSlide",
  components: {
    SmartImage,
    SmartText,
    ArrowButton,
    WYSIWYG,
    MainButton,
  },
})
export default class HeroAlternativeSlide extends Mixins(onResize) {
  @Prop() data!: HomePageHeroAlternativeBlockType;
  @Prop() length!: number; // Total number of slides
  @Prop() index!: number; // Current slide index (0-based)
  @Prop() selectedIndex!: number;

  @Ref() titleRef: SmartText;
  @Ref() subtitleRef: SmartText;
  @Ref() leftArrowRef: ArrowButton;
  @Ref() rightArrowRef: ArrowButton;
  @Ref() wysiwygRef: WYSIWYG;
  @Ref() buttonRef: MainButton;

  // Watch for when the slide becomes active or inactive
  @Watch("selectedIndex")
  onSelectedIndexChanged() {
    if (this.isActiveSlide) {
      this.enterAnimation();
    } else {
      this.leaveAnimation();
    }
  }

  rootClass = "c-home-hero-alternative-slide";

  get title() {
    return this.data.title;
  }

  get subtitle() {
    return this.data.subtitle;
  }

  get description() {
    return this.data.description;
  }

  get image(): SmartImageProps {
    return {
      image: this.data.image,
      fit: "cover",
      lazyload: true,
    };
  }

  get link(): ThemeLink {
    return this.data.link;
  }

  get isFirstSlide() {
    return this.index === 0;
  }

  get isLastSlide() {
    return this.index === this.length - 1;
  }

  get isActiveSlide() {
    return this.index === this.selectedIndex;
  }

  get elementsToAnim() {
    return [
      this.titleRef.$el,
      this.subtitleRef.$el,
      !this.isFirstSlide ? this.leftArrowRef.$el : null, // Exclude leftArrowRef if first slide
      !this.isLastSlide ? this.rightArrowRef.$el : null, // Exclude rightArrowRef if last slide
      this.wysiwygRef.$el,
      this.buttonRef.$el,
    ].filter((el) => el !== null);
  }

  mounted() {
    if (this.isMob) return;
    GSAP.set(this.elementsToAnim, {
      opacity: 0, // Hide elements at the start
      y: 30,
    });

    if (this.isActiveSlide) {
      this.enterAnimation();
    }
  }

  enterAnimation() {
    if (this.isMob) return;
    GSAP.timeline().to(this.elementsToAnim, {
      opacity: 1,
      y: 0,
      stagger: 0.1,
    });
  }

  leaveAnimation() {
    if (this.isMob) return;
    GSAP.timeline().to(this.elementsToAnim, {
      opacity: 0,
      y: 30,
    });
  }
}
